/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useDisclosure } from "@jumbo/components/JumboDialog/hooks/useDisclosure";
import DialogConfirm from "@jumbo/components/JumboDialog/components/DialogConfirm";
import DialogDefault from "@jumbo/components/JumboDialog/components/DialogDefault";

import Stack from "@mui/material/Stack";
import {
  Button,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
  Box,
} from "@mui/material";
import { Card, CardContent } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ClearIcon from "@mui/icons-material/Clear";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported";

import { usePagination } from "app/hooks/usePagination";
import { Table } from "app/components";
import Div from "@jumbo/shared/Div";
import { useDispatch, useSelector } from "react-redux";
import {
  markModuleRead,
  fetchUnreadCount,
} from "app/redux/slice/notificationSlice";
import Loader from "@jumbo/components/Loader";
import { userType } from "app/helpers/userType";
import {
  PRODUCT_ADD,
  productEdit,
  officeProductVariant,
} from "app/routes/constant";
import { useNavigate } from "react-router-dom";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import {
  deleteProduct,
  getProductList,
  importProduct,
  toggleProductStatus,
} from "app/redux/slice/productSlice";
import { useSnackbar } from "notistack";
import { CloudDownload } from "@mui/icons-material";
import axios from "axios";

const Product = () => {
  const { stageAction } = useDisclosure();
  const {
    isOpen: isImportOpen,
    onOpen: openImport,
    onClose: closeImport,
  } = useDisclosure();
  const dispatch = useDispatch();
  const { isMA } = userType();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const productData = useSelector((state) => state?.product);

  const [searchText, setSearchText] = useState("");
  const [activeFilter, setActiveFilter] = useState("All");
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("createdAt");

  const {
    isOpen: isDelete,
    onClose: closeDelete,
    actionProps: deleteActionProps,
    stageAction: deleteStageAction,
  } = useDisclosure();

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination({ id: "uniqueVariant" });

  const [permissionData] = useState(
    JSON.parse(localStorage.getItem("permission")),
  );

  const [permission, setPermission] = useState({
    createPermission: false,
    updatePermission: false,
    deletePermission: false,
    listVariantPermission: false,
  });

  const getPermission = () => {
    if (isMA) {
      setPermission({
        createPermission: true,
        updatePermission: true,
        deletePermission: true,
        listVariantPermission: true,
      });
    } else {
      let permissionValue = {
        createPermission: false,
        updatePermission: false,
        deletePermission: false,
        listVariantPermission: false,
      };

      permissionData?.length &&
        permissionData.forEach(function (num) {
          if (num?.name === "create-product") {
            permissionValue.createPermission = true;
          }
          if (num?.name === "delete-product") {
            permissionValue.deletePermission = true;
          }
          if (num?.name === "update-product") {
            permissionValue.updatePermission = true;
          }
          if (num?.name === "list-variant") {
            permissionValue.listVariantPermission = true;
          }
        });
      setPermission(permissionValue);
    }
  };

  const [isImporting, setImporting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importError, setImportError] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importErrorsList, setImportErrorsList] = useState([]);
  const [importErrorFileUrl, setImportErrorFileUrl] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const columns = [
    {
      id: "images",
      label: "Photo",
      minWidth: 60,
      render: (row) => {
        const main = row?.images?.[0];
        return main ? (
          <Box
            component="img"
            src={`${process.env.REACT_APP_IMAGE_BASE_URL}product-images/${main}`}
            alt={row?.name}
            sx={{ width: 44, height: 44, objectFit: "cover", borderRadius: 1 }}
          />
        ) : (
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 1,
              bgcolor: "action.hover",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ImageNotSupportedIcon fontSize="small" color="disabled" />
          </Box>
        );
      },
    },
    { id: "name", label: "Name", sort: true },
    { id: "prefix", label: "Sku", sort: true },
    { id: "quantity", label: "Qty", sort: true },
    {
      id: "gsm",
      label: "GSM",
      sort: true,
      // Null when no weight has been recorded - shown as a dash rather than 0,
      // which would read as "weighs nothing".
      render: (row) => (row?.gsm === null || row?.gsm === undefined ? "-" : row.gsm),
    },
    {
      id: "isActive",
      label: "App Status",
      minWidth: 130,
      align: "center",
      // Off = hidden in the app. Everything on this side keeps working, which
      // is why the label says "App Status" and not just "Status".
      render: (row) => (
        <Switch
          checked={row?.isActive !== false}
          disabled={!permission.updatePermission}
          onChange={() => handleToggleStatus(row)}
          color="success"
          size="small"
          title={
            row?.isActive !== false
              ? "Visible in the app - click to hide"
              : "Hidden in the app - click to show"
          }
        />
      ),
    },
    {
      id: "kg",
      label: "KG",
      // Only products with Set KG turned on carry a weight. A dash means the
      // app falls back to its own default, which is deliberately not shown as
      // a number here - that default lives in the app, not in this database,
      // so printing it would go stale the day the app changes it.
      render: (row) =>
        row?.isKgSet && row?.kg != null ? (
          <Chip size="small" label={`${row.kg} kg`} color="success" />
        ) : (
          "-"
        ),
    },
    {
      id: "price",
      label: "Price",
      sort: true,
      render: (row) => row?.price ?? 0,
    },
    {
      id: "collection",
      label: "Collection",
      render: (row) => row?.collection?.name ?? "-",
    },
    {
      id: "actions",
      label: "Actions",
      minWidth: 140,
      align: "center",
      render: (row) => (
        <>
          <IconButton
            disabled={!permission.updatePermission}
            onClick={() => {
              stageAction(row);
              navigate(productEdit(row._id));
            }}
          >
            <EditIcon />
          </IconButton>
          <Tooltip title="Variants List">
            <IconButton
              disabled={!permission.listVariantPermission}
              onClick={() => {
                stageAction(row);
                navigate(officeProductVariant(row._id));
              }}
            >
              <FormatListBulletedIcon />
            </IconButton>
          </Tooltip>
          <IconButton
            disabled={!permission.deletePermission}
            onClick={() => {
              deleteStageAction(row);
            }}
          >
            <DeleteIcon color={"error"} />
          </IconButton>
        </>
      ),
    },
  ];

  const DeleteProduct = () => {
    closeDelete();
    dispatch(deleteProduct(deleteActionProps?._id))
      .unwrap()
      .then((res) => {
        if (res?.status === 200) {
          enqueueSnackbar(res.data?.responseMessage, {
            variant: "success",
          });
          getData();
          closeDelete();
        } else {
          enqueueSnackbar(res.data?.responseMessage, {
            variant: "error",
          });
        }
      });
  };

  const handleFileSelect = (e) => {
    setImportError(null);
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate extension
    const validTypes = [
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];
    if (!validTypes.includes(file.type)) {
      setImportError("Please upload a Excel file.");
      setSelectedFile(null);
      return;
    }
    // Validate size - 5MB
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setImportError("File size should be less than 5MB.");
      setSelectedFile(null);
      return;
    }
    setSelectedFile(file);
  };

  const handleImportConfirm = () => {
    if (!selectedFile) {
      setImportError("Please select a file to import.");
      return;
    }
    setImporting(true);
    const formData = new FormData();
    formData.append("file", selectedFile);
    dispatch(importProduct(formData))
      .unwrap()
      .then((res) => {
        if (res?.status === 200) {
          enqueueSnackbar(res.data?.responseMessage, { variant: "success" });
          // show import summary in dialog instead of closing immediately
          const detail =
            res.data?.data?.productDetail ?? res.data?.data ?? null;
          setImportResult(detail);
          setImportErrorsList(
            res.data?.data?.productDetail?.errors ??
              res.data?.data?.errors ??
              res.data?.data?.errorList ??
              detail?.errors ??
              [],
          );
          setImportErrorFileUrl(
            res.data?.data?.productDetail?.errorFileUrl ??
              res.data?.data?.errorFileUrl ??
              res.data?.data?.productDetail?.errorFilePath ??
              res.data?.data?.errorFilePath ??
              null,
          );
          // refresh list in background
          getData();
        } else {
          enqueueSnackbar(res.data?.responseMessage, { variant: "error" });
        }
      })
      .catch(() => {
        enqueueSnackbar("Import failed", { variant: "error" });
      })
      .finally(() => setImporting(false));
  };

  const handleImportCancel = () => {
    setSelectedFile(null);
    setImportError(null);
    setImportResult(null);
    setImportErrorsList([]);
    setImportErrorFileUrl(null);
    closeImport();
  };

  const handleImportDone = () => {
    setSelectedFile(null);
    setImportError(null);
    setImportResult(null);
    setImportErrorsList([]);
    setImportErrorFileUrl(null);
    closeImport();
  };

  const handleToggleStatus = async (row) => {
    const next = row?.isActive === false;
    const res = await dispatch(
      toggleProductStatus({ id: row._id, status: next }),
    ).unwrap();
    if (res?.status === 200) {
      enqueueSnackbar(res.data?.responseMessage, { variant: "success" });
      getData();
    } else {
      enqueueSnackbar(
        res?.data?.responseMessage || "Could not change the status",
        { variant: "error" },
      );
    }
  };

  const getData = () => {
    localStorage.setItem("product-search", searchText.trim());
    const value = {
      page: page,
      perPage: rowsPerPage,
      search: searchText.trim(),
      // Active / Deactivated / both. This only changes what Admin sees here -
      // the app hides deactivated products whatever is chosen.
      ...(activeFilter !== "All" && {
        isActive: activeFilter === "Active" ? "true" : "false",
      }),
    };
    if (orderBy !== "") {
      value.sort = order === "asc" ? 1 : -1;
      value.field = orderBy;
    }
    dispatch(getProductList(value));
    // Mark as read whenever list is fetched
    dispatch(markModuleRead("Product")).then(() => {
      dispatch(fetchUnreadCount("Product"));
    });
  };

  // Download CSV from API by sending csv=1 as a query param
  const downloadCsv = async () => {
    setIsDownloading(true);
    const params = {
      page: page,
      perPage: rowsPerPage,
      search: searchText.trim(),
      // Active / Deactivated / both. This only changes what Admin sees here -
      // the app hides deactivated products whatever is chosen.
      ...(activeFilter !== "All" && {
        isActive: activeFilter === "Active" ? "true" : "false",
      }),
    };
    if (orderBy !== "") {
      params.sort = order === "asc" ? 1 : -1;
      params.field = orderBy;
    }
    params.csv = 1;

    try {
      const response = await axios.get("product/list", {
        params,
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "text/csv",
      });

      let filename = "products.csv";
      const disposition = response.headers["content-disposition"];
      if (disposition) {
        const match = disposition.match(
          /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
        );
        if (match != null && match[1]) filename = match[1].replace(/['"]/g, "");
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("CSV download failed:", error);
      // eslint-disable-next-line no-alert
      alert("CSV download failed. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleChangePage(0, 0);
      getData();
    }
  };

  const sortData = (Order, OrderBy) => {
    setOrder(Order);
    setOrderBy(OrderBy);
  };

  useEffect(() => {
    getPermission();
  }, []);

  useEffect(() => {
    getData();
  }, [page, rowsPerPage, dispatch, order, orderBy]);

  return (
    <>
      {productData?.loading ? (
        <Loader />
      ) : (
        <>
          <Card sx={{ display: "flex", mb: 3 }}>
            <Div sx={{ display: "flex", flexDirection: "column", flex: "1" }}>
              <CardContent>
                <Grid container spacing={{ xs: 1, md: 3 }}>
                  <Grid item xs={12} sm={7}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Search"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      onKeyPress={handleKeyPress}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment
                            position="end"
                            style={{
                              display: searchText === "" ? "none" : "flex",
                            }}
                            onClick={() => setSearchText("")}
                          >
                            <ClearIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>App Status</InputLabel>
                      <Select
                        value={activeFilter}
                        label="App Status"
                        onChange={(e) => setActiveFilter(e.target.value)}
                      >
                        <MenuItem value={"All"}>All</MenuItem>
                        <MenuItem value={"Active"}>Active</MenuItem>
                        <MenuItem value={"Deactivated"}>Deactivated</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Button
                      fullWidth
                      type="submit"
                      variant="contained"
                      onClick={() => {
                        handleChangePage(0, 0);
                        getData();
                      }}
                    >
                      Submit
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Div>
          </Card>
          <Card>
            <CardContent>
              <Stack
                justifyContent="space-between"
                direction={{ xs: "column", sm: "row" }}
                sx={{ mb: 3 }}
              >
                <Typography variant={"h2"} mb={0}>
                  Product List
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    justifyContent: "flex-end",
                    mt: { xs: 1, md: 0 },
                  }}
                >
                  {permission.createPermission && (
                    <LoadingButton
                      onClick={() => {
                        navigate(PRODUCT_ADD);
                      }}
                      type="submit"
                      variant="contained"
                    >
                      Add Product
                    </LoadingButton>
                  )}
                  {permission.createPermission && (
                    <Button
                      variant="outlined"
                      startIcon={<CloudUploadIcon />}
                      onClick={() => {
                        openImport();
                      }}
                      disabled={isImporting}
                    >
                      Import
                    </Button>
                  )}
                  {permission.createPermission && (
                    <LoadingButton
                      onClick={downloadCsv}
                      loading={isDownloading}
                      variant="outlined"
                      startIcon={<CloudDownload />}
                    >
                      Download CSV
                    </LoadingButton>
                  )}
                </Box>
              </Stack>
              <Table
                columns={columns}
                rows={productData?.list}
                page={page}
                rowsPerPage={rowsPerPage}
                Order={order}
                count={productData?.total}
                OrderBy={orderBy}
                sortData={sortData}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                editAction={stageAction}
              />
            </CardContent>
          </Card>
          <DialogConfirm
            isOpen={isDelete}
            onClose={closeDelete}
            title="Are you sure you want to delete the product?"
            onConfirm={DeleteProduct}
            btnLabels={{ cancel: "Cancel", confirm: "Delete" }}
          />
          <DialogDefault
            open={isImportOpen}
            onClose={handleImportCancel}
            title="Import Products"
            fullWidth
            maxWidth="sm"
            content={
              importResult ? (
                <div>
                  <Typography variant="h6" gutterBottom>
                    Import Summary
                  </Typography>
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    <Typography>
                      Products Created:{" "}
                      {importResult?.productsCreated ??
                        importResult?.products_created ??
                        0}
                    </Typography>
                    <Typography>
                      Variants Created:{" "}
                      {importResult?.variantsCreated ??
                        importResult?.variants_created ??
                        0}
                    </Typography>
                    <Typography>
                      Total Processed:{" "}
                      {importResult?.totalProcessed ??
                        importResult?.total_processed ??
                        0}
                    </Typography>
                    <Typography>
                      Has Errors:{" "}
                      {importResult?.hasErrors || importErrorsList?.length > 0
                        ? "Yes"
                        : "No"}
                    </Typography>

                    {importErrorsList?.length > 0 && (
                      <div>
                        <Typography variant="subtitle2" sx={{ mt: 1 }}>
                          Errors:
                        </Typography>
                        <ul>
                          {importErrorsList.slice(0, 10).map((err, idx) => (
                            <li key={idx}>
                              <Typography variant="body2">
                                {typeof err === "string"
                                  ? err
                                  : JSON.stringify(err)}
                              </Typography>
                            </li>
                          ))}
                        </ul>
                        {importErrorsList.length > 10 && (
                          <Typography variant="caption">
                            Showing first 10 errors
                          </Typography>
                        )}
                      </div>
                    )}

                    {importErrorFileUrl && (
                      <Button
                        href={importErrorFileUrl}
                        target="_blank"
                        rel="noreferrer"
                        sx={{ mt: 1 }}
                      >
                        Download Error File
                      </Button>
                    )}
                  </Stack>
                </div>
              ) : (
                <div>
                  <a
                    href="/product_data.xlsx"
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      color: "#1976d2",
                      textDecoration: "underline",
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <CloudDownload fontSize="small" />
                    Download Sample File
                  </a>

                  <Typography variant="body1" gutterBottom>
                    Upload a Excel file to import product data. Allowed file
                    types: XLS, XLSX Max size: 5MB.
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    sx={{ mt: 2 }}
                  >
                    <Button component="label" variant="contained">
                      Choose File
                      <input
                        hidden
                        accept=", application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        type="file"
                        onChange={(e) => handleFileSelect(e)}
                      />
                    </Button>
                    <Typography variant="body2">
                      {selectedFile ? selectedFile.name : "No file selected"}
                    </Typography>
                  </Stack>
                  {importError && (
                    <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                      {importError}
                    </Typography>
                  )}
                </div>
              )
            }
            actions={
              importResult ? (
                <>
                  <Button onClick={handleImportDone} color="inherit">
                    Done
                  </Button>
                  {importErrorFileUrl && (
                    <Button
                      href={importErrorFileUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Download Errors
                    </Button>
                  )}
                </>
              ) : (
                <>
                  <Button onClick={handleImportCancel} color="inherit">
                    Cancel
                  </Button>
                  <LoadingButton
                    onClick={handleImportConfirm}
                    loading={isImporting}
                    variant="contained"
                    disabled={!selectedFile || isImporting}
                  >
                    Upload
                  </LoadingButton>
                </>
              )
            }
          />
        </>
      )}
    </>
  );
};

export default Product;
